import { useEffect, useState } from "react";
import { api, ApiError } from "./api";
import { naytaPvm } from "./pvm";
import { t, virheTeksti } from "./kieli";
import type { KaannosAvain } from "./kaannokset";

/* Linjan luovutusaineisto samalla linjatunnuksella (loppudokumentaation vaihe 1, demo 12.11.; Toni 4.11.2026 klo 08.50 ja 10.10 kohta 5):
   01/01 ainestodistukset · 01/07 NDT-pöytäkirjat · 02/<media>/04 koeponnistuspöytäkirja · 02/<media>/05 linjatarkastus. Kansiopolut tulevat
   palvelimelta (ydin, asiakirja.ts). Tyhjä osio sanoo olevansa tyhjä, ei katoa. */

type Osio =
  | { laji: "ainestodistus"; polku: string | null; sulatuksia: number; katettu: number; rivit: { sulatus: string; todistukset: { id: number; liitteita: number }[] }[] }
  | { laji: "ndt_poytakirja"; polku: string | null; rivit: { menetelma: string; raportti: string | null; hitseja: number; hyvaksytty: number; hylatty: number; poytakirja_id: number | null; liitteita: number }[] }
  | { laji: "koeponnistuspoytakirja"; polku: string | null; tiedostonimi: string | null;
      rivi: { piiri_id: number; piiri: string; tulos: string | null; paiva: string | null; poytakirja_tila: string | null } | null }
  | { laji: "linjatarkastuspoytakirja"; polku: string | null; rivi: { id: number; tila: string; pvm: string | null; allekirjoitettu: boolean } | null };
type Aineisto = { linja: { id: number; tunnus: string; media: string | null }; osiot: Osio[] };

export default function LinjanLuovutusaineisto({ linjaId }: { linjaId: number }) {
  const [a, setA] = useState<Aineisto | null>(null);
  const [vika, setVika] = useState<string | null>(null);
  useEffect(() => {
    setA(null); setVika(null);
    api.linjanLuovutusaineisto(linjaId).then((r) => setA(r as Aineisto))
      .catch((e) => setVika(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)));
  }, [linjaId]);
  if (vika) return <p className="huom huom--vika" role="alert">{vika}</p>;
  if (!a) return null;
  const sisalto = (o: Osio) => {
    switch (o.laji) {
      case "ainestodistus":
        if (o.sulatuksia === 0) return <span className="dim">{t("lua.ei_sulatuksia")}</span>;
        return <>{t("lua.sulatuksia_katettu", { katettu: o.katettu, n: o.sulatuksia })}
          {o.katettu < o.sulatuksia && <span className="dim"> · {t("lua.puuttuu")}: {o.rivit.filter((r) => r.todistukset.length === 0).map((r) => r.sulatus).join(", ")}</span>}</>;
      case "ndt_poytakirja":
        if (o.rivit.length === 0) return <span className="dim">{t("lua.ei_ndt")}</span>;
        return <>{o.rivit.map((r, i) => (
          <div key={i} data-ndt-poytakirja={r.raportti ?? ""}>
            {r.menetelma} · {r.raportti ?? <span className="dim">{t("lua.ei_numeroa")}</span>} · {t("lua.hitseja", { n: r.hitseja })}
            {r.hylatty > 0 && <> · {t("lua.hylattyja", { n: r.hylatty })}</>}
            {r.raportti && <span className="dim"> · {r.liitteita > 0 ? t("lua.liitteita", { n: r.liitteita }) : t("lua.ei_liitetta")}</span>}
          </div>))}</>;
      case "koeponnistuspoytakirja":
        if (!o.rivi) return <span className="dim">{t("lua.ei_koepiiria")}</span>;
        return <>{t("kpk.piiri")} {o.rivi.piiri} · {o.rivi.tulos ? t(`kpk.tulos_${o.rivi.tulos}` as KaannosAvain) : t("kpk.ei_koetta")}
          {o.rivi.paiva && <> · {naytaPvm(o.rivi.paiva)}</>} · {o.rivi.poytakirja_tila === "allekirjoitettu" ? t("kpk.tila_valmis")
            : o.rivi.poytakirja_tila ? t("kpk.tila_odottaa_lyhyt") : t("lua.ei_poytakirjaa")}
          {o.tiedostonimi && <div className="dim">{o.tiedostonimi}</div>}</>;
      case "linjatarkastuspoytakirja":
        if (!o.rivi) return <span className="dim">{t("lua.ei_linjatarkastusta")}</span>;
        return <>{o.rivi.tila === "hyvaksytty" ? t("lua.lt_hyvaksytty") : t("lua.lt_kesken")}{o.rivi.pvm && <> · {naytaPvm(o.rivi.pvm)}</>}
          {" · "}{o.rivi.allekirjoitettu ? t("lua.allekirjoitus_liitteena") : <span className="dim">{t("lua.ei_allekirjoitusta")}</span>}</>;
    }
  };
  return (
    <section className="panel" data-luovutusaineisto={a.linja.tunnus} style={{ borderLeft: "3px solid var(--muste)" }}>
      <h4>{t("lua.otsikko")}: {a.linja.tunnus}</h4>
      <table className="taulu" data-rivit={a.osiot.length} data-joukko="luovutusaineisto">
        <thead><tr><th>{t("lua.kansio")}</th><th>{t("lua.osio")}</th><th>{t("lua.tila")}</th></tr></thead>
        <tbody>{a.osiot.map((o) => (
          <tr key={o.laji} data-luovutusosio={o.laji}>
            <td><code>{o.polku ?? t("lua.media_puuttuu")}</code></td>
            <td>{t(`lua.laji_${o.laji}` as KaannosAvain)}</td>
            <td>{sisalto(o)}</td>
          </tr>))}</tbody>
      </table>
    </section>
  );
}
